import {
    ActionCreatorWithPayload,
    CaseReducer,
    createAsyncThunk,
} from '@reduxjs/toolkit'
import {
    NinchatEventType,
    NinchatSessionCreatedEvent,
} from 'api/types/events'
import {
    NinchatProperties,
    NinchatStore,
} from 'api/types'
import {NinchatEventActionPayload} from 'api/NinchatClient'
import {RootState} from 'store'
import {cacheService} from 'utils/cache.service'
import {extractProperties} from '.'
import {initialState} from 'api/slice'

const eventTypePrefix = 'NINCHAT/event'

type SessionCreatedAsyncThunk = typeof thunk & {reducer: CaseReducer} & SessionCreatedActionCreator

type SessionCreatedActionCreator = ActionCreatorWithPayload<NinchatEventActionPayload<NinchatSessionCreatedEvent>, 'NINCHAT/event/session_created'>

type SessionCreatedAsyncThunkConfig = {
    state: RootState
}

const thunk = createAsyncThunk<NinchatSessionCreatedEvent, NinchatSessionCreatedEvent, SessionCreatedAsyncThunkConfig>(
    `${eventTypePrefix}/${NinchatEventType.NinchatSessionCreatedEvent}`,
    (event) => {
        cacheService.clear()

        return event
    },
)

export const sessionCreated = thunk as SessionCreatedAsyncThunk

sessionCreated.reducer = (state, action) => {
    const {...properties} = extractProperties<NinchatSessionCreatedEvent>(action.payload)

    const user_auth = properties.user_auth ?? state.user_auth

    const {
        user_id,
        user_realms,
        user_transcripts,
        ...rest
    } = properties

    const _user_audience_transcripts = Object.assign({}, ...(user_transcripts || [])
        .filter((transcript) => 'audience_id' in transcript)
        .map((transcript) => ({
            [transcript.audience_id]: {
                assign_user_id: user_id, // populate assign_user_id with session users id
                ...transcript,
            },
        })))

    const _user_realms = Object.entries(user_realms).reduce((a: NinchatProperties.NinchatUserRealms, [realm_id, realm_attrs]) => {
        a[realm_id] = {realm_attrs} as NinchatProperties.NinchatUserRealm
        return a
    }, {})

    // Session resumption replaces server-backed state, but locally pending sends
    // still need their original request IDs so their eventual replies can reconcile.
    const _messages = state.user_id === user_id
        ? Object.entries(state._messages as NinchatStore.NinchatMessagesStore).reduce<NinchatStore.NinchatMessagesStore>((result, [chatId, {messages}]) => {
            const outgoingMessages = messages.filter((message) => message._message_status === 'pending' || message._message_status === 'rejected')
            if (outgoingMessages.length) {
                result[chatId] = {messages: outgoingMessages}
            }
            return result
        }, {})
        : {}

    return {
        ...initialState,
        ...rest,
        _messages,
        _session_revision: (state._session_revision ?? 0) + 1,
        _user_audience_transcripts,
        user_auth,
        user_id,
        user_realms: _user_realms,
    }
}
